import { useEffect, useMemo, useRef } from 'react';

export type Size = {
  width: number;
  height: number;
};

export type UseResizeObserverParams = ResizeObserverOptions & {
  onResize: (size: Size) => void;
};

export function useResizeObserver<T extends HTMLElement = HTMLElement>({
  onResize,
  box = 'border-box',
}: UseResizeObserverParams) {
  const frameID = useRef(0);
  const ref = useRef<T | null>(null);

  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  const observer = useMemo(
    () =>
      typeof window !== 'undefined'
        ? new ResizeObserver((entries) => {
            const entry = entries[0];

            if (entry) {
              cancelAnimationFrame(frameID.current);

              frameID.current = requestAnimationFrame(() => {
                if (ref.current) {
                  let width = 0;
                  let height = 0;
                  switch (box) {
                    case 'border-box': {
                      const borderBoxSize = Array.isArray(entry.borderBoxSize)
                        ? entry.borderBoxSize[0]
                        : entry.borderBoxSize
                          ? entry.borderBoxSize
                          : {
                              inlineSize: entry.contentRect.width,
                              blockSize: entry.contentRect.height,
                            };

                      width = borderBoxSize.inlineSize;
                      height = borderBoxSize.blockSize;

                      break;
                    }
                    case 'content-box':
                      width = entry.contentRect.width;
                      height = entry.contentRect.height;

                      break;
                    case 'device-pixel-content-box': {
                      const devicePixelContentBoxSize = Array.isArray(
                        entry.devicePixelContentBoxSize,
                      )
                        ? entry.devicePixelContentBoxSize[0]
                        : entry.devicePixelContentBoxSize
                          ? entry.devicePixelContentBoxSize
                          : {
                              inlineSize: entry.contentRect.width,
                              blockSize: entry.contentRect.height,
                            };
                      width = devicePixelContentBoxSize.inlineSize;
                      height = devicePixelContentBoxSize.blockSize;

                      break;
                    }
                  }

                  onResizeRef.current?.({ width, height });
                }
              });
            }
          })
        : null,
    [box],
  );

  useEffect(() => {
    if (ref.current) {
      observer?.observe(ref.current, { box });
    }

    return () => {
      observer?.disconnect();

      if (frameID.current) {
        cancelAnimationFrame(frameID.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current, observer, box]);

  return [ref] as const;
}
